import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import 
{
  Mail, Phone, UserCheck,
  User, MapPin, Map, Globe, Hash, Link,
  CircleCheckBig
} from "lucide-react";
import { CreateLead, GetSourceMetaData } from "../Services/LeadService";
import{ FetchPinCodeDetails} from "../../../../../services/CommonServices";
import { useNavigate } from "react-router-dom";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";

const InputField = ({
  label,
  required,
  placeholder,
  icon: Icon,
  value,
  onChange,
  error,
  readOnly,
  maxLength,
}) => {
  return (
    <div className="space-y-1">
      <label className="text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className={`flex items-center border rounded-xl px-3 py-2 shadow-sm transition
                    ${
                      readOnly
                        ? "bg-gray-100 border-gray-200"
                        : "bg-white border-gray-200 focus-within:ring-2 focus-within:ring-blue-500"
                    }`
                  }
      >
        {Icon && <Icon className="text-gray-400 mr-2" size={18} />}
        <input
          value={value}
          onChange={onChange}
          type="text"
          placeholder={placeholder}
          readOnly={readOnly}
          maxLength={maxLength}
          className={`w-full bg-transparent outline-none text-sm ${
                        readOnly
                          ? "cursor-not-allowed text-gray-500 select-none"
                          : "text-gray-800"
                      }`
                    }
        />
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

const AddLead = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFormState = {
    name: "",
    phoneNo: "",
    email: "",
    gender: "",
    pinCode: "",
    city: "",
    state: "",
    country: "",
    source: "",

    leadUID: "",
    currentLocation: "",
    qualification: "",
    totalExperience: "",
    currentCompany: "",
    designation: "",
    skills: "",
    appliedFor: "",
    preferredLocation: "",
    currentCTC: null,
    expectedCTC: null,
    reasonOfLeaving: "",
    department: "",
    assignedTo: "",
  };
  const [errors, setErrors] = useState({});
  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [alert, setAlert] = useState(null);
  const [phoneNo, setPhoneNo] = useState("");
  const [isFetchingPinCode, setIsFetchingPinCode] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const skipPinCodeEffect = useRef(false);

  const [sourceOptions, setSourceOptions] = useState([]);
  const [filteredSources, setFilteredSources] = useState([]);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [activeSourceIndex, setActiveSourceIndex] = useState(-1);

  useEffect(() => {
    fetchSourceMetaData();
  }, []);

  const fetchSourceMetaData = async () => {
    try {
      const res = await GetSourceMetaData();

      if (res?.status === "Success") {
        setSourceOptions(res?.data || []);
        setFilteredSources(res?.data || []);
      }
    } catch (error) {
      setSourceOptions([]);
      setFilteredSources([]);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const phone = params.get("phone");

    // if (!phone) {
    //   // optional safety fallback
    //   navigate("/dashboard");
    //   return;
    // }
    setPhoneNo(phone);
  }, []);

  useEffect(() => {
  if (location.state?.lead) {
     skipPinCodeEffect.current = true;
    const leadData = location.state.lead;

    setForm((prev) => ({
      ...prev,

      name: leadData.name || "",
      email: leadData.email || "",
      gender: leadData.gender || "",
      pinCode: leadData.pinCode || "",
      city: leadData.city || "",
      state: leadData.state || "",
      country: leadData.country || "",
      source: leadData.source || "",
    }));
  }
}, [location.state]);

  const fetchPinCodeDetails = async (pinCode) => {
    if(pinCode.length !== 6) return;

    try {
      setIsFetchingPinCode(true);
      const res = await FetchPinCodeDetails(pinCode);
      if (res?.status === "success") {
        const cityList =
          res?.city
            ?.split(",")
            ?.map((item) => item.trim())
            ?.filter(Boolean) || [];

        setCityOptions(cityList);
        setFilteredCities(cityList);

        setForm((prev) => ({
          ...prev,
          city: cityList[0] || "",
          state: res.state || "",
          country: res.country || "",
        }));

        setErrors((prev) => ({
          ...prev,
          pinCode: "",
        }));
      } else {
        setCityOptions([]);
        setFilteredCities([]);
        setErrors((prev) => ({
          ...prev,
          pinCode: "Could not fetch location details. Please enter manually.",
        }));
      }
    } catch (error) {
      setCityOptions([]);
      setFilteredCities([]);

      const errorMessage =
        error?.message === "No data found for this pincode."
          ? "No location found for this pincode. Please enter manually."
          : "Pincode service unavailable. Please enter location details manually.";

      setErrors((prev) => ({
        ...prev,
        pinCode: errorMessage,
      }));
    } finally {
      setIsFetchingPinCode(false);
    }
  };

  useEffect(() => {
    if(form.pinCode?.length !== 6) return;
    if (skipPinCodeEffect.current) {
      skipPinCodeEffect.current = false;
      return;
    }
    if(form.pinCode === "999999")
    {
      setCityOptions([]);
      setFilteredCities([]);

      setForm((prev) => ({
        ...prev,
        city: "Other",
        state: "Other",
        country: "India"
      }));

      setErrors((prev) => ({
        ...prev,
        pinCode: ""
      }));

      return
    }
    fetchPinCodeDetails(form.pinCode);
  }, [form.pinCode]);

  const validateLeads = () => {
    let newErrors = {};

    if (!form.name.trim()) newErrors.name = "Full Name is required";
    if (form.email.trim()) 
    {
      if (!/^\S+@\S+\.\S+$/.test(form.email)) 
      {
        newErrors.email = "Invalid email format";
      }
    }
    if (!form.city.trim()) newErrors.city = "City is required";
    if (!form.state.trim()) newErrors.state = "State is required";
    if (!form.country.trim()) newErrors.country = "Country is required";
    if (!form.source.trim()) newErrors.source = "Source is required";
    if (!form.gender.trim()) newErrors.gender = "Gender is required";
    if (!form.pinCode.trim()) {
      newErrors.pinCode = "Pin code is required";
    } else if (!/^[0-9]{6}$/.test(form.pinCode)) {
      newErrors.pinCode = "Invalid pin code";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
      if (!validateLeads()) return;

      const payload = {
        name: form.name,
        phoneNo: phoneNo,
        email: form.email,
        gender: form.gender,
        pincode: form.pinCode,
        city: form.city,
        state: form.state,
        country: form.country,
        source: form.source,
      };

      try {
        const res = await CreateLead(payload);

        if (res?.status === "success") {
          setAlert({
            type: "success",
            message: res?.message || "Lead created successfully",
          });

          setLead(res.leadUID);
          setTimeout(() => {
            navigate(`/Edit-Lead/${res.leadUID}`, { replace: true })
          }, 1500);
        } else {
          setAlert({
            type: "error",
            message: res?.message || "Something went wrong",
          });
        }
      } catch (err) {
        setAlert({
          type: "error",
          message:
            err?.response?.data?.message ||
            err?.response?.data ||
            err?.message ||
            "Server error",
        });
      }
    
  };

  const handleChange = (key) => (e) => {
    let value = e.target.value;
    if(key === "pinCode")
    {
      value = value.replace(/\D/g, "");
    }
    setForm({ ...form, [key]: value });
  };

  const resetForm = () => {
    setForm(initialFormState);
    setErrors({});
    setLead(null);
  };

  return (
    <>
      <div className="p-6">
        <div className="max-w-8xl mx-auto bg-white rounded-3xl shadow-lg overflow-visible">
          {/* Header */}
          <div className="relative overflow-hidden border-b border-slate-200 bg-white px-8 py-6">
            {/* Background Glow */}
            <div className="absolute top-0 right-0 h-32 w-32 rounded-full bg-blue-100 blur-3xl opacity-60"></div>

            <div className="relative flex items-center justify-between">
              {/* LEFT SECTION */}
              <div className="flex items-center gap-4">
                {/* ICON BOX */}
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 shadow-sm">
                  <UserCheck className="text-blue-600" size={28} />
                </div>

                {/* TEXT */}
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-slate-800">
                      Basic Details
                    </h2>

                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100">
                      Step 1
                    </span>
                  </div>

                  <p className="mt-1 text-sm text-slate-500">
                    Enter essential candidate information for lead creation
                  </p>
                </div>
              </div>

              {/* RIGHT STATUS */}
              <div className="hidden md:flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2">
                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>

                <span className="text-sm font-medium text-slate-600">
                  Recruitment CRM
                </span>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* CONTACT INFO */}
            <div>
              <div className="grid md:grid-cols-3 gap-5">
                <InputField
                  label="Full Name"
                  required
                  placeholder="Enter Full Name"
                  icon={User}
                  value={form.name}
                  onChange={handleChange("name")}
                  error={errors.name}
                />

                <div>
                  <InputField
                    label="Phone Number"
                    required
                    placeholder="+91 9876543210"
                    icon={Phone}
                    value={phoneNo}
                    onChange={handleChange("phoneNo")}
                    error={errors.phoneNo}
                    readOnly
                  />

                  {phoneNo && (
                    <p className="text-xs text-green-500 mt-1 ml-1 flex gap-2 items-center">
                      <CircleCheckBig size={14} />
                      Verified
                    </p>
                  )}
                </div>

                <InputField
                  label="Email Address"
                  placeholder="Enter Email"
                  icon={Mail}
                  value={form.email}
                  onChange={handleChange("email")}
                  error={errors.email}
                />

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    Gender <span className="text-red-500">*</span>
                  </label>

                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
                    <User className="text-gray-400 mr-2" size={18} />

                    <select
                      value={form.gender}
                      onChange={handleChange("gender")}
                      className="w-full bg-transparent outline-none text-sm"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  {errors.gender && (
                    <p className="text-xs text-red-500">{errors.gender}</p>
                  )}
                </div>

                <InputField
                  label="Pin Code"
                  required
                  placeholder="Enter Pincode"
                  icon={Hash}
                  value={form.pinCode}
                  onChange={handleChange("pinCode")}
                  error={errors.pinCode}
                  maxLength={6}
                />

                <div className="space-y-1 relative">
                  <label className="text-sm font-semibold text-gray-700">
                    City <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2 bg-white  shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
                    <MapPin className="text-gray-400" size={18} />
                    <input
                      type="text"
                      value={form.city}
                      placeholder="Enter or select city."
                      className="w-full bg-transparent outline-none text-sm"
                      onFocus={() => {
                        setShowCityDropdown(true);
                        setFilteredCities(cityOptions);
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setShowCityDropdown(false);
                        }, 150);
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((prev) => ({
                          ...prev,
                          city: value,
                        }));

                        const filtered = cityOptions.filter((city) =>
                          city.toLowerCase().includes(value.toLowerCase()),
                        );

                        setFilteredCities(filtered);
                        setShowCityDropdown(true);
                      }}
                    />
                  </div>
                  {showCityDropdown && filteredCities.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {filteredCities.map((city, index) => (
                        <div
                          key={index}
                          onMouseDown={() => {
                            setForm((prev) => ({
                              ...prev,
                              city,
                            }));
                            setShowCityDropdown(false);
                          }}
                          className="px-4 py-2 text-sm cursor-pointer transition hover:bg-gray-100"
                        >
                          {city}
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.city && (
                    <p className="text-xs text-red-500">{errors.city}</p>
                  )}
                </div>

                <InputField
                  label="State"
                  required
                  placeholder="Enter State"
                  icon={Map}
                  value={form.state}
                  onChange={handleChange("state")}
                  error={errors.state}
                />

                <InputField
                  label="Country"
                  required
                  placeholder="Enter Country"
                  icon={Globe}
                  value={form.country}
                  onChange={handleChange("country")}
                  error={errors.country}
                />
              </div>
            </div>

            {/* SOURCE */}
            <div className="space-y-1 relative">
              <label className="text-sm font-semibold text-gray-700">
                Source <span className="text-red-500">*</span>
              </label>

              <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
                <Link className="text-gray-400 mr-2" size={18} />

                <input
                  type="text"
                  value={form.source || ""}
                  placeholder="Type or select source"
                  className="w-full bg-transparent outline-none text-sm"
                  onFocus={() => {
                    setShowSourceDropdown(true);
                    setFilteredSources(sourceOptions);
                    setActiveSourceIndex(-1);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowSourceDropdown(false);
                    }, 150);
                  }}
                  onChange={(e) => {
                    const value = e.target.value;

                    setForm((prev) => ({
                      ...prev,
                      source: value,
                    }));

                    const filtered = sourceOptions.filter((item) =>
                      item.source.toLowerCase().includes(value.toLowerCase()),
                    );

                    setFilteredSources(filtered);

                    setShowSourceDropdown(true);

                    setActiveSourceIndex(-1);
                  }}
                  onKeyDown={(e) => {
                    if (!showSourceDropdown) return;

                    if (e.key === "ArrowDown") {
                      e.preventDefault();

                      setActiveSourceIndex((prev) =>
                        prev < filteredSources.length - 1 ? prev + 1 : 0,
                      );
                    }

                    if (e.key === "ArrowUp") {
                      e.preventDefault();

                      setActiveSourceIndex((prev) =>
                        prev > 0 ? prev - 1 : filteredSources.length - 1,
                      );
                    }

                    if (e.key === "Enter") {
                      e.preventDefault();

                      if (
                        activeSourceIndex >= 0 &&
                        filteredSources[activeSourceIndex]
                      ) {
                        setForm((prev) => ({
                          ...prev,
                          source: filteredSources[activeSourceIndex].source,
                        }));
                      }

                      setShowSourceDropdown(false);
                      setActiveSourceIndex(-1);
                    }

                    if (e.key === "Escape") {
                      setShowSourceDropdown(false);
                      setActiveSourceIndex(-1);
                    }
                  }}
                />
              </div>

              {showSourceDropdown && filteredSources.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
                  {filteredSources.map((item, index) => (
                    <div
                      key={item.id || index}
                      onMouseDown={() => {
                        setForm((prev) => ({
                          ...prev,
                          source: item.source,
                        }));

                        setShowSourceDropdown(false);

                        setActiveSourceIndex(-1);
                      }}
                      className={`px-4 py-2 text-sm cursor-pointer transition ${
                                  index === activeSourceIndex
                                    ? "bg-blue-100 text-blue-700"
                                    : "hover:bg-gray-100"
                                }`}
                    >
                      {item.source}
                    </div>
                  ))}
                </div>
              )}

              {showSourceDropdown &&
                form.source &&
                filteredSources.length === 0 &&
                sourceOptions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-2 z-[9999] bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-2 text-sm text-gray-500">
                    No matches found — you can use your entered value
                  </div>
                )}

              {errors.source && (
                <p className="text-xs text-red-500">{errors.source}</p>
              )}
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex justify-end gap-4 px-8 pb-6">
            <button
              onClick={resetForm}
              className="px-6 py-3 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
            >
              Reset
            </button>

            <button
              onClick={handleSubmit}
              className="px-8 py-3 rounded-xl bg-primary text-white font-semibold shadow-md transition"
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default AddLead;
